"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { createSupabaseBrowserClient } from "@/lib/supabase/browser";

type Me = { id: string; nickname: string | null; isAdmin: boolean } | null;

export function useMe() {
  const [me, setMe] = useState<Me>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const supabase = createSupabaseBrowserClient();
    let alive = true;

    const load = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!alive) return;
      if (!user) {
        setMe(null);
        setLoaded(true);
        return;
      }
      const [{ data: profile }, { data: adminRow }] = await Promise.all([
        supabase
          .from("member_profiles")
          .select("nickname")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("admin_users")
          .select("user_id")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);
      if (!alive) return;
      setMe({
        id: user.id,
        nickname: profile?.nickname ?? null,
        isAdmin: Boolean(adminRow),
      });
      setLoaded(true);
    };

    void load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void load();
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { me, loaded };
}

export default function MemberMenu({
  variant,
  onNavigate,
}: {
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { me, loaded } = useMe();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // 드롭다운 위치 (버튼 기준, 화면 고정 좌표)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const next = encodeURIComponent(pathname || "/reviews");

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (rect)
        setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
    };
  }, [open]);

  const logout = async () => {
    await createSupabaseBrowserClient().auth.signOut();
    setOpen(false);
    onNavigate?.();
    router.refresh();
  };

  const withdraw = async () => {
    if (
      !window.confirm(
        "정말 탈퇴하시겠습니까?\n작성한 고객 이야기와 사진이 모두 삭제되며 복구할 수 없습니다.",
      )
    )
      return;
    const res = await fetch("/api/member", { method: "DELETE" });
    if (!res.ok) {
      const j = await res.json().catch(() => null);
      window.alert(j?.error ?? "탈퇴 처리 중 오류가 발생했습니다.");
      return;
    }
    await createSupabaseBrowserClient().auth.signOut();
    setOpen(false);
    onNavigate?.();
    router.push("/");
    router.refresh();
  };

  if (!loaded)
    return variant === "desktop" ? <div data-component-id="src/components/common/MemberMenu.tsx:136:35" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="136" data-component-end-line="136" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B132px%5D%22%7D" className="w-[132px]" /> : null;

  const label = me ? (me.nickname ?? (me.isAdmin ? "관리자" : "회원")) : "";

  if (variant === "mobile") {
    return (
      <div data-component-id="src/components/common/MemberMenu.tsx:142:6" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="142" data-component-end-line="192" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20pb-3%20space-y-2%22%7D" className="px-4 pb-3 space-y-2">
        {me ? (
          <>
            <div data-component-id="src/components/common/MemberMenu.tsx:145:12" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="145" data-component-end-line="147" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-2%20text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22%EB%8B%98%22%7D" className="px-2 text-sm text-text-secondary">
              <span data-component-id="src/components/common/MemberMenu.tsx:146:14" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="146" data-component-end-line="146" data-component-file="MemberMenu.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-text-primary%22%7D" className="font-semibold text-text-primary">{label}</span>님
            </div>
            {me.isAdmin ? (
              <Link data-component-id="src/components/common/MemberMenu.tsx:149:14" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="149" data-component-end-line="155" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fadmin%22%2C%22className%22%3A%22block%20w-full%20px-4%20py-3%20text-center%20border%20border-border%20rounded-lg%20font-medium%22%2C%22textContent%22%3A%22%EA%B4%80%EB%A6%AC%EC%9E%90%20%ED%99%94%EB%A9%B4%22%7D"
                href="/admin"
                onClick={onNavigate}
                className="block w-full px-4 py-3 text-center border border-border rounded-lg font-medium"
              >
                관리자 화면
              </Link>
            ) : null}
            <div data-component-id="src/components/common/MemberMenu.tsx:157:12" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="157" data-component-end-line="172" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%22%7D" className="flex gap-2">
              <button data-component-id="src/components/common/MemberMenu.tsx:158:14" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="158" data-component-end-line="163" data-component-file="MemberMenu.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20border%20border-border%20rounded-lg%20font-medium%22%2C%22textContent%22%3A%22%EB%A1%9C%EA%B7%B8%EC%95%84%EC%9B%83%22%7D"
                onClick={logout}
                className="flex-1 px-4 py-3 border border-border rounded-lg font-medium"
              >
                로그아웃
              </button>
              {!me.isAdmin ? (
                <button data-component-id="src/components/common/MemberMenu.tsx:165:16" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="165" data-component-end-line="170" data-component-file="MemberMenu.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20border%20border-border%20rounded-lg%20text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22%ED%9A%8C%EC%9B%90%ED%83%88%ED%87%B4%22%7D"
                  onClick={withdraw}
                  className="flex-1 px-4 py-3 border border-border rounded-lg text-sm text-text-secondary"
                >
                  회원탈퇴
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <div data-component-id="src/components/common/MemberMenu.tsx:175:10" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="175" data-component-end-line="190" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%22%7D" className="flex gap-2">
            <Link data-component-id="src/components/common/MemberMenu.tsx:176:12" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="176" data-component-end-line="182" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20text-center%20border%20border-border%20rounded-lg%20font-medium%22%2C%22textContent%22%3A%22%EB%A1%9C%EA%B7%B8%EC%9D%B8%22%7D"
              href={`/login?next=${next}`}
              onClick={onNavigate}
              className="flex-1 px-4 py-3 text-center border border-border rounded-lg font-medium"
            >
              로그인
            </Link>
            <Link data-component-id="src/components/common/MemberMenu.tsx:183:12" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="183" data-component-end-line="189" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fsignup%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20text-center%20border%20border-border%20rounded-lg%20font-medium%22%2C%22textContent%22%3A%22%ED%9A%8C%EC%9B%90%EA%B0%80%EC%9E%85%22%7D"
              href="/signup"
              onClick={onNavigate}
              className="flex-1 px-4 py-3 text-center border border-border rounded-lg font-medium"
            >
              회원가입
            </Link>
          </div>
        )}
      </div>
    );
  }

  if (!me) {
    return (
      <div data-component-id="src/components/common/MemberMenu.tsx:198:6" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="198" data-component-end-line="211" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20ml-1%20lg%3Aml-2%22%7D" className="flex items-center gap-1 ml-1 lg:ml-2">
        <Link data-component-id="src/components/common/MemberMenu.tsx:199:8" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="199" data-component-end-line="204" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22px-3%20py-2%20text-sm%20font-medium%20text-text-primary%20hover%3Atext-primary%20rounded-lg%20transition-smooth%22%2C%22textContent%22%3A%22%EB%A1%9C%EA%B7%B8%EC%9D%B8%22%7D"
          href={`/login?next=${next}`}
          className="px-3 py-2 text-sm font-medium text-text-primary hover:text-primary rounded-lg transition-smooth"
        >
          로그인
        </Link>
        <Link data-component-id="src/components/common/MemberMenu.tsx:205:8" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="205" data-component-end-line="210" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fsignup%22%2C%22className%22%3A%22px-3%20py-2%20text-sm%20font-medium%20border%20border-border%20rounded-lg%20hover%3Abg-secondary%20transition-smooth%22%2C%22textContent%22%3A%22%ED%9A%8C%EC%9B%90%EA%B0%80%EC%9E%85%22%7D"
          href="/signup"
          className="px-3 py-2 text-sm font-medium border border-border rounded-lg hover:bg-secondary transition-smooth"
        >
          회원가입
        </Link>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/common/MemberMenu.tsx:216:4" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="216" data-component-end-line="261" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20ml-1%20lg%3Aml-2%22%7D" ref={boxRef} className="relative ml-1 lg:ml-2">
      <button data-component-id="src/components/common/MemberMenu.tsx:217:6" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="217" data-component-end-line="224" data-component-file="MemberMenu.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-2%20text-sm%20font-medium%20border%20border-border%20rounded-lg%20hover%3Abg-secondary%20transition-smooth%20max-w-%5B9rem%5D%20truncate%22%2C%22textContent%22%3A%22%EB%8B%98%22%7D"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 text-sm font-medium border border-border rounded-lg hover:bg-secondary transition-smooth max-w-[9rem] truncate"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}님
      </button>
      {open && pos
        ? createPortal(
            // 헤더(z-nav) 밖으로 꺼내 시세 티커(z-nav)와 시세/BGM 버튼(z-[9000]) 위에 표시. 공지 팝업(z-[9999])보다는 아래.
            <div data-component-id="src/components/common/MemberMenu.tsx:228:12" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="228" data-component-end-line="257" data-component-file="MemberMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20z-%5B9500%5D%20w-40%20rounded-xl%20border%20border-border%20bg-white%20shadow-conversion%20overflow-hidden%22%7D"
              ref={menuRef}
              role="menu"
              className="fixed z-[9500] w-40 rounded-xl border border-border bg-white shadow-conversion overflow-hidden"
              style={{ top: pos.top, right: pos.right }}
            >
              {me.isAdmin ? (
                <Link data-component-id="src/components/common/MemberMenu.tsx:235:16" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="235" data-component-end-line="241" data-component-file="MemberMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fadmin%22%2C%22className%22%3A%22block%20px-4%20py-3%20text-sm%20hover%3Abg-secondary%22%2C%22textContent%22%3A%22%EA%B4%80%EB%A6%AC%EC%9E%90%20%ED%99%94%EB%A9%B4%22%7D"
                  href="/admin"
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-sm hover:bg-secondary"
                >
                  관리자 화면
                </Link>
              ) : null}
              <button data-component-id="src/components/common/MemberMenu.tsx:243:14" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="243" data-component-end-line="248" data-component-file="MemberMenu.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22block%20w-full%20text-left%20px-4%20py-3%20text-sm%20hover%3Abg-secondary%22%2C%22textContent%22%3A%22%EB%A1%9C%EA%B7%B8%EC%95%84%EC%9B%83%22%7D"
                onClick={logout}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-secondary"
              >
                로그아웃
              </button>
              {!me.isAdmin ? (
                <button data-component-id="src/components/common/MemberMenu.tsx:250:16" data-component-path="src/components/common/MemberMenu.tsx" data-component-line="250" data-component-end-line="255" data-component-file="MemberMenu.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22block%20w-full%20text-left%20px-4%20py-3%20text-sm%20text-text-secondary%20hover%3Abg-secondary%20border-t%20border-border%22%2C%22textContent%22%3A%22%ED%9A%8C%EC%9B%90%ED%83%88%ED%87%B4%22%7D"
                  onClick={withdraw}
                  className="block w-full text-left px-4 py-3 text-sm text-text-secondary hover:bg-secondary border-t border-border"
                >
                  회원탈퇴
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
